import { memo, type SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { ClipType } from '@air/api/types';
import { buildClientImgixUrl } from '@air/component-imgix-image';
import { cn } from '@air/utils-tailwind';

import { getCloudFrontUrlFromImgixUrl, getImagePreviewFallbackUrl } from '~/constants/cloudfront';
import { useInlineSvgPreview } from '~/hooks/useInlineSvgPreview';
import {
  centralizedClipAssetsSelector,
  centralizedClipExtSelector,
  centralizedClipSelector,
  centralizedClipTypeSelector,
} from '~/store/centralizedClip/selectors';
import { getClipImageAlt } from '~/utils/getClipImageAlt';
import {
  canRetryTransparentPreview,
  getTransparentPreviewSource,
  isSvgExtension,
  TRANSPARENT_PREVIEW_IMGIX_PARAMS,
  type TransparentPreviewSource,
  transparentPreviewDisplaySrc,
} from '~/utils/getTransparentPreviewSource';

const DEFAULT_IMG_SIZE = 800;

type MobilePreviewImage = {
  fallbackUrl: string;
  preserveTransparency: boolean;
  processedPreviewSrc: string | undefined;
  src: string;
};

type MobilePreviewSelection = {
  clipType: ClipType;
  image: string;
  transparentPreview: TransparentPreviewSource;
  video: string | undefined;
};

const selectMobilePreviewImage = ({
  clipType,
  image,
  transparentPreview,
  video,
}: MobilePreviewSelection): MobilePreviewImage => {
  const { fallbackSrc, preserveTransparency: previewKeepsAlpha, src: originalSrc } = transparentPreview;
  let src = image;
  let preserveTransparency = false;
  const useAnimated = clipType === ClipType.animated && Boolean(video);

  if (useAnimated && video) {
    src = video;
  }

  if (!useAnimated && previewKeepsAlpha && originalSrc) {
    src = originalSrc;
    preserveTransparency = true;
  }

  const fallbackSource = preserveTransparency ? (fallbackSrc ?? src) : src;
  const processedPreviewSrc = preserveTransparency && fallbackSrc && fallbackSrc !== src ? fallbackSrc : undefined;

  return {
    fallbackUrl: preserveTransparency
      ? getImagePreviewFallbackUrl(fallbackSource)
      : getCloudFrontUrlFromImgixUrl(fallbackSource),
    preserveTransparency,
    processedPreviewSrc,
    src,
  };
};

export const MobileImageVisualizer = memo(() => {
  const clipAssets = useSelector(centralizedClipAssetsSelector);
  const clipExt = useSelector(centralizedClipExtSelector);
  const clip = useSelector(centralizedClipSelector);
  const imageAlt = getClipImageAlt(clip, 'displayTitle');
  const clipType = useSelector(centralizedClipTypeSelector);
  const [failedSrc, setFailedSrc] = useState<string | undefined>();
  const [failedUrl, setFailedUrl] = useState<string | undefined>();
  const imageRef = useRef<HTMLImageElement>(null);
  const transparentPreview = useMemo(() => getTransparentPreviewSource(clipExt, clipAssets), [clipAssets, clipExt]);

  const { src, fallbackUrl, preserveTransparency, processedPreviewSrc } = useMemo(
    () =>
      selectMobilePreviewImage({
        clipType,
        transparentPreview,
        image: clipAssets.image,
        video: clipAssets.video,
      }),
    [clipAssets.image, clipAssets.video, clipType, transparentPreview],
  );

  const displaySrc = transparentPreviewDisplaySrc({ failedSrc, processedPreviewSrc, src }) ?? src;
  const showCheckerboard = preserveTransparency && displaySrc === src;

  const imgxUrl = useMemo(() => {
    return buildClientImgixUrl(
      displaySrc,
      {
        w: DEFAULT_IMG_SIZE,
        h: DEFAULT_IMG_SIZE,
        auto: 'format',
        ...(showCheckerboard ? TRANSPARENT_PREVIEW_IMGIX_PARAMS : {}),
      },
      { src: displaySrc, disableSrcSet: true },
    );
  }, [displaySrc, showCheckerboard]);

  // Gate on the Imgix URL, not `displayUrl`, so a failed Imgix original stops the fetch without flipping back and forth.
  const { inlineSvgPreviewUrl, markInlineSvgPreviewFailed, isInlineSvgPreviewPending } = useInlineSvgPreview({
    src,
    ext: clipExt,
    enabled: showCheckerboard && failedUrl !== imgxUrl,
  });

  // Hold the raw SVG back until normalization settles, so it never paints and then swaps.
  const isAwaitingInlineSvgPreview = showCheckerboard && isInlineSvgPreviewPending;

  // Imgix returns SVG originals raw (no fonts, no intrinsic size); the normalized data URL replaces it once ready.
  const displayUrl = isAwaitingInlineSvgPreview ? '' : (showCheckerboard && inlineSvgPreviewUrl) || imgxUrl;

  // An intentionally empty URL while the SVG preview is pending is a loading state, not an error.
  const error = !isAwaitingInlineSvgPreview && (!displayUrl || failedUrl === displayUrl);

  const handleError = useCallback(
    (event?: SyntheticEvent<HTMLImageElement>) => {
      // A failed data URL falls back to the Imgix original; only Imgix failures drop transparency.
      const erroredSrc = event ? event.currentTarget.currentSrc || event.currentTarget.src : displayUrl;

      if (showCheckerboard && inlineSvgPreviewUrl && erroredSrc === inlineSvgPreviewUrl) {
        markInlineSvgPreviewFailed(event);

        return;
      }

      if (canRetryTransparentPreview({ failedSrc, processedPreviewSrc, src })) {
        setFailedSrc(src);

        return;
      }

      setFailedUrl(displayUrl);
    },
    [
      failedSrc,
      displayUrl,
      inlineSvgPreviewUrl,
      markInlineSvgPreviewFailed,
      processedPreviewSrc,
      showCheckerboard,
      src,
    ],
  );

  useEffect(() => {
    // Viewbox-only SVGs can report `naturalWidth` 0 in WebKit/Firefox, which would drop transparency before the
    // normalized data URL lands; real load errors still reach `onError`.
    // Once normalization gives up (settled with no preview), the guard resumes so the raw SVG can fall back.
    if (
      error ||
      !displayUrl ||
      (isSvgExtension(clipExt) && showCheckerboard && (isInlineSvgPreviewPending || displayUrl === inlineSvgPreviewUrl))
    ) {
      return;
    }

    const image = imageRef.current;

    if (image?.complete && image.naturalWidth === 0) {
      handleError();
    }
  }, [clipExt, displayUrl, error, handleError, inlineSvgPreviewUrl, isInlineSvgPreviewPending, showCheckerboard]);

  return (
    <div className="flex size-full max-h-[calc(100vh-238px)] items-center justify-center" data-testid="IMAGE_THEATER">
      {error && fallbackUrl ? (
        // biome-ignore lint/performance/noImgElement: visualizer fallback cannot use next/image
        <img
          alt={imageAlt}
          className={cn(
            'flex max-h-full max-w-full origin-center object-contain',
            showCheckerboard && 'transparency-checkerboard',
          )}
          src={fallbackUrl}
        />
      ) : (
        // biome-ignore lint/performance/noImgElement: visualizer cannot use next/image
        <img
          alt={imageAlt}
          className={cn(
            'flex max-h-full max-w-full origin-center object-contain',
            showCheckerboard && 'transparency-checkerboard',
            // Hide the src-less box and its alt text while the SVG preview is pending.
            isAwaitingInlineSvgPreview && 'invisible',
          )}
          onError={handleError}
          ref={imageRef}
          // An empty src fires `error`; omitting it keeps the pending SVG out of the error/fallback path.
          src={isAwaitingInlineSvgPreview ? undefined : displayUrl}
        />
      )}
    </div>
  );
});

MobileImageVisualizer.displayName = 'MobileImageVisualizer';
